import type { ReactNode } from 'react';

/**
 * The two-column frame every full-page authentication screen sits in.
 *
 * <h2>Why this exists rather than each page composing its own</h2>
 *
 * The ported sign-in and registration pages each carry their own copy of this
 * grid — the same gradient, the same five blurred blobs, the same tech grid at
 * the same opacity, the same three animated shapes. Copying it a third and a
 * fourth time for activation would make four places to change when the panel
 * changes, and three of them would be missed.
 *
 * <p>The classes are theirs, deliberately: `bg-white`, `text-gray-900`,
 * `bg-gray-50`. Those look hard-coded and are not — the stylesheet brought across
 * with the design carries `.dark .bg-white`, `.dark .text-gray-900` and the rest,
 * so a screen written in the same vocabulary follows the theme exactly as sign-in
 * does. Written in this project's own tokens instead, these pages would be the
 * two that went the wrong colour when somebody switched to dark.
 *
 * <h2>The form comes first on a small screen</h2>
 *
 * The panel is decoration: on a phone it would push the thing the person came to
 * do below a screen and a half of illustration. `order-1` on the form column and
 * `order-2` on the panel puts the work first when the columns stack, and the
 * `lg:` orders restore the intended side at width.
 */
export interface AuthSplitScreenProps {
  /** The form, message or outcome — the reason the page exists. */
  children: ReactNode;
  /** The illustration stack shown on the panel, above its heading. */
  illustration: ReactNode;
  /** The panel's heading. */
  headline: string;
  /** The paragraph under it. */
  blurb: string;
  /**
   * Which side the panel takes at `lg` and above. Sign-in puts it left and
   * registration puts it right; the activation screens continue registration, so
   * they keep its arrangement.
   */
  panelSide?: 'left' | 'right';
}

export function AuthSplitScreen({
  children,
  illustration,
  headline,
  blurb,
  panelSide = 'right',
}: AuthSplitScreenProps) {
  const panelIsLeft = panelSide === 'left';

  return (
    <div className="min-h-screen bg-gray-50 glass-none">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
        <div
          className={`order-1 flex flex-col justify-center bg-white p-8 lg:p-8 ${
            panelIsLeft ? 'lg:order-2' : 'lg:order-1'
          }`}
        >
          <div className="mx-auto w-full max-w-md">{children}</div>
        </div>

        <div
          className={`relative order-2 flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-8 lg:p-12 ${
            panelIsLeft
              ? 'lg:order-1 border-r border-white/10'
              : 'lg:order-2 border-l border-white/10'
          }`}
        >
          <PanelBackground />

          <div className="relative z-10 max-w-md text-center text-white">
            <div className="mb-8 space-y-6">
              {illustration}
              <SecurityIndicators />
            </div>

            <h2 className="g-color mb-4 text-2xl font-bold">{headline}</h2>
            <p className="g-color leading-relaxed text-white/80">{blurb}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The blobs, the grid and the three floating shapes.
 *
 * <p>Purely decorative, and marked so: `aria-hidden` keeps eleven empty divs out
 * of the accessibility tree, where they would otherwise be announced as nothing
 * at all between the heading and the form.
 */
function PanelBackground() {
  return (
    <div aria-hidden className="absolute inset-0 z-0">
      <div className="absolute top-32 left-32 h-64 w-64 rounded-full bg-blue-400/30 blur-3xl"></div>
      <div className="absolute top-20 right-32 h-80 w-80 rounded-full bg-indigo-400/25 blur-3xl"></div>
      <div className="absolute bottom-32 left-64 h-48 w-96 rounded-full bg-cyan-400/20 blur-3xl"></div>
      <div className="absolute top-64 right-64 h-48 w-48 rounded-full bg-purple-400/20 blur-2xl"></div>
      <div className="absolute right-20 bottom-20 h-32 w-32 rounded-full bg-teal-400/25 blur-xl"></div>

      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* `animate-ping` at four seconds, which is the original's timing. It is
          motion on a decorative element, so it is dropped entirely rather than
          slowed for anyone who asked for less of it -- see index.css. */}
      <div
        className="absolute top-20 left-20 h-14 w-14 rotate-45 rounded-lg border border-purple-400/25 animate-ping"
        style={{ animationDuration: '4s' }}
      ></div>
      <div
        className="absolute top-32 right-24 h-24 w-24 rotate-45 rounded-lg border border-purple-400/25 animate-ping"
        style={{ animationDuration: '4s' }}
      ></div>
      <div className="absolute bottom-40 left-1/4 h-20 w-20 rounded-full bg-gradient-to-r from-blue-500/15 to-indigo-500/15 blur-sm"></div>
    </div>
  );
}

/** The three badges that close every panel in the ported design. */
function SecurityIndicators() {
  return (
    <div aria-hidden className="flex justify-center space-x-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
        <span className="text-xl text-green-400">✓</span>
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
        <span className="text-lg text-blue-400">🔐</span>
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
        <span className="text-lg text-purple-400">⚡</span>
      </div>
    </div>
  );
}
